import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { PageShell } from "@/components/EmptyStateWrapper";
import { FAQ_CATEGORIES, getFaqCategory } from "@/lib/faqContent";

function FaqIndex() {
    return (
        <PageShell flow className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
            <header className="mb-8">
                <p className="label-eyebrow">Help</p>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">FAQ</h1>
                <p className="mt-2 text-sm text-white/50 max-w-xl">
                    Pick a topic to see common questions and answers about FairShare.
                </p>
            </header>

            <ul className="grid gap-3 sm:grid-cols-2" data-testid="faq-category-list">
                {FAQ_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    return (
                        <li key={cat.id}>
                            <Link
                                to={`/faq/${cat.id}`}
                                className="group flex h-full items-start gap-3 rounded-xl border border-white/[0.07] bg-[#232323] p-4 transition-colors hover:border-white/15 hover:bg-[#282828]"
                                data-testid={`faq-category-${cat.id}`}
                            >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#00BF0D]">
                                    <Icon className="h-5 w-5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center justify-between gap-2">
                                        <span className="font-medium text-white group-hover:text-[#00BF0D] transition-colors">
                                            {cat.title}
                                        </span>
                                        <ChevronRight className="h-4 w-4 shrink-0 text-white/30 group-hover:text-white/55" />
                                    </span>
                                    <span className="mt-1 block text-xs text-white/45 leading-relaxed">
                                        {cat.description}
                                    </span>
                                    <span className="mt-2 block text-[10px] font-mono text-white/30">
                                        {cat.items.length} questions
                                    </span>
                                </span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </PageShell>
    );
}

function FaqCategoryView() {
    const { categoryId } = useParams();
    const category = getFaqCategory(categoryId);

    if (!category) {
        return <Navigate to="/faq" replace />;
    }

    const Icon = category.icon;

    return (
        <PageShell flow className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
            <Link
                to="/faq"
                className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6 transition-colors"
                data-testid="faq-back"
            >
                <ArrowLeft className="h-4 w-4" /> All topics
            </Link>

            <header className="mb-6 flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#00BF0D]/25 bg-[#00BF0D]/10 text-[#00BF0D]">
                    <Icon className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl tracking-tight">{category.title}</h1>
                    <p className="mt-1 text-sm text-white/50">{category.description}</p>
                </div>
            </header>

            <ul className="space-y-2.5" data-testid={`faq-items-${category.id}`}>
                {category.items.map((item, idx) => (
                    <li key={idx}>
                        <details
                            className="group rounded-xl border border-white/[0.07] bg-[#232323] open:border-white/12 open:bg-[#282828] transition-colors"
                            data-testid={`faq-item-${category.id}-${idx}`}
                        >
                            <summary className="cursor-pointer list-none px-4 py-3.5 text-sm font-medium text-white marker:content-none flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
                                <span>{item.q}</span>
                                <ChevronRight className="h-4 w-4 shrink-0 text-white/35 transition-transform group-open:rotate-90" />
                            </summary>
                            <div className="border-t border-white/[0.06] px-4 py-3.5 text-sm text-white/60 leading-relaxed">
                                {item.a}
                            </div>
                        </details>
                    </li>
                ))}
            </ul>
        </PageShell>
    );
}

export function FaqPage() {
    return (
        <Routes>
            <Route index element={<FaqIndex />} />
            <Route path=":categoryId" element={<FaqCategoryView />} />
        </Routes>
    );
}
